import React, { useCallback, useEffect, useState } from 'react';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import {
  Button,
  Callout,
  Dialog,
  EmptyState,
  Fact,
  Field,
  IconRefresh,
  Input,
  ListPage,
  Pagination,
  Radio,
  Row,
  SectionTitle,
  Segmented,
  Spinner,
  StatusLabel,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  Textarea,
  TH,
  THead,
  TR,
  TwoLine,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import { TableSearch } from './TableSearch';
import {
  canIssueRefund,
  canMarkReviewed,
  emptyNoun,
  formatCents,
  formatRefundedAt,
  formatReviewedAt,
  initialRefundAmount,
  MARK_OUTCOME_OPTIONS,
  type MarkOutcome,
  outcomeLabel,
  outcomeTone,
  parseRefundReviewPage,
  REFUND_REVIEW_PAGE_SIZE,
  type RefundReviewDetail,
  type RefundReviewRow,
  remainingCents,
  requestErrorMessage,
  REVIEWED_FILTER_ITEMS,
  type ReviewedFilter,
  statusTone,
  validateRefundAmount,
} from './refundReviewModel';

const Dash: React.FC = () => <span className="text-v2-fg-4">—</span>;

type RefundSortKey =
  | 'id'
  | 'store_name'
  | 'total_price'
  | 'refunded_total'
  | 'status'
  | 'external_status'
  | 'outcome';

const REFUND_SORT_KEY_TO_FIELD: Record<RefundSortKey, string> = {
  id: 'id',
  store_name: 'store_name',
  total_price: 'total_price',
  refunded_total: 'refunded_total',
  status: 'status',
  external_status: 'external_status',
  outcome: 'outcome',
};

export default function RefundReviewTab() {
  // List state
  const [rows, setRows] = useState<RefundReviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [reviewedFilter, setReviewedFilter] = useState<ReviewedFilter>('false');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const [sort, setSort] = useState<TableSortState<RefundSortKey>>({
    key: null,
    direction: 'desc',
  });

  // Detail dialog
  const [detailOrder, setDetailOrder] = useState<RefundReviewDetail | null>(
    null,
  );
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState<RefundReviewRow | null>(
    null,
  );
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');

  // Mark reviewed dialog
  const [markTarget, setMarkTarget] = useState<RefundReviewRow | null>(null);
  const [markOutcome, setMarkOutcome] =
    useState<MarkOutcome>('reviewed_no_refund');
  const [markNotes, setMarkNotes] = useState('');
  const [markLoading, setMarkLoading] = useState(false);
  const [markError, setMarkError] = useState('');

  // ── Fetch list ───────────────────────────────────────────────────────────────

  const fetchRows = useCallback(
    async (pg: number, reviewed: ReviewedFilter) => {
      setLoading(true);
      try {
        const response = await api.get(
          apiEndpoints.getRefundReviews(
            pg,
            REFUND_REVIEW_PAGE_SIZE,
            reviewed,
            undefined,
            toOrderingParam(sort, REFUND_SORT_KEY_TO_FIELD) ?? undefined,
            debouncedSearch || undefined,
          ),
        );
        if (response && response.ok) {
          const result = parseRefundReviewPage(await response.json());
          setRows(result.rows);
          setTotalCount(result.totalCount);
          setTotalPages(result.totalPages);
        } else {
          setRows([]);
        }
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, sort],
  );

  useEffect(() => {
    fetchRows(page, reviewedFilter);
  }, [page, reviewedFilter, fetchRows]);

  const filtering = debouncedSearch.length > 0;

  const handleFilterChange = (filter: ReviewedFilter) => {
    setReviewedFilter(filter);
    setPage(1);
  };

  // ── Fetch detail ─────────────────────────────────────────────────────────────

  const openDetail = async (orderId: number) => {
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const response = await api.get(
        apiEndpoints.getRefundReviewDetail(orderId),
      );
      if (response && response.ok) {
        setDetailOrder(await response.json());
      }
    } catch {
      // silently fail
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailOrder(null);
  };

  // ── Refund dialog ────────────────────────────────────────────────────────────

  const openRefundModal = (row: RefundReviewRow) => {
    setRefundTarget(row);
    setRefundAmountInput(initialRefundAmount(row));
    setRefundNotes('');
    setRefundError('');
  };

  const closeRefundModal = () => {
    setRefundTarget(null);
    setRefundError('');
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    setRefundLoading(true);
    setRefundError('');
    try {
      const check = validateRefundAmount(refundAmountInput, refundTarget);
      if (!check.ok) {
        setRefundError(check.error);
        setRefundLoading(false);
        return;
      }
      const body: Record<string, any> = { notes: refundNotes };
      body.amount_cents = check.amountCents;
      const response = await api.post(
        apiEndpoints.issueRefund(refundTarget.order_id),
        body,
      );
      if (response && response.ok) {
        closeRefundModal();
        fetchRows(page, reviewedFilter);
        if (detailOpen && detailOrder?.order_id === refundTarget.order_id) {
          openDetail(refundTarget.order_id);
        }
      } else if (response) {
        setRefundError(await requestErrorMessage(response));
      }
    } catch {
      setRefundError('Network error — please try again.');
    } finally {
      setRefundLoading(false);
    }
  };

  // ── Mark reviewed dialog ─────────────────────────────────────────────────────

  const openMarkModal = (row: RefundReviewRow) => {
    setMarkTarget(row);
    setMarkOutcome('reviewed_no_refund');
    setMarkNotes('');
    setMarkError('');
  };

  const closeMarkModal = () => {
    setMarkTarget(null);
    setMarkError('');
  };

  const submitMark = async () => {
    if (!markTarget) return;
    setMarkLoading(true);
    setMarkError('');
    try {
      const response = await api.post(
        apiEndpoints.markRefundReviewed(markTarget.order_id),
        { outcome: markOutcome, notes: markNotes },
      );
      if (response && (response.status === 201 || response.ok)) {
        closeMarkModal();
        fetchRows(page, reviewedFilter);
      } else if (response) {
        setMarkError(await requestErrorMessage(response));
      }
    } catch {
      setMarkError('Network error — please try again.');
    } finally {
      setMarkLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const showOutcome = reviewedFilter === 'true';
  const columnCount = showOutcome ? 8 : 7;
  const noun = emptyNoun(reviewedFilter);

  return (
    <>
      <ListPage
        title="Refund review"
        toolbar={
          <div className="flex min-h-11 flex-wrap items-center gap-3 px-4 py-2 lg:px-6">
            <TableSearch
              label="Search refund reviews"
              placeholder="Search store, order, or status…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <Segmented
              aria-label="Review status"
              items={REVIEWED_FILTER_ITEMS}
              value={reviewedFilter}
              onChange={handleFilterChange}
              className="shrink-0"
            />
          </div>
        }
      >
        {!loading && rows.length === 0 ? (
          <EmptyState
            icon={<IconRefresh />}
            title={
              filtering
                ? 'No refund reviews match that search'
                : `You don't have any ${noun} at the moment`
            }
            description={
              filtering
                ? 'Try a store, order id, or status.'
                : `When ${noun} appear, they'll show up here.`
            }
          />
        ) : (
          <>
            {loading && (
              <p role="status" className="sr-only">
                Loading…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table fixed className="min-w-[1100px]">
                <THead>
                  <tr>
                    {(
                      [
                        ['id', 'Order', 128],
                        ['store_name', 'Store', undefined],
                        ['total_price', 'Total', 104],
                        ['refunded_total', 'Refunded', 128],
                        ['status', 'Status', 140],
                        ['external_status', 'External status', 140],
                      ] as const
                    ).map(([key, label, width]) => (
                      <TH
                        key={key}
                        width={width}
                        align={
                          key === 'total_price' || key === 'refunded_total'
                            ? 'right'
                            : undefined
                        }
                        sortable={!loading}
                        active={sort.key === key}
                        dir={sort.direction}
                        onSort={() => {
                          setSort((current) => toggleSortState(current, key));
                          setPage(1);
                        }}
                      >
                        {label}
                      </TH>
                    ))}
                    {showOutcome && (
                      <TH
                        width={120}
                        sortable={!loading}
                        active={sort.key === 'outcome'}
                        dir={sort.direction}
                        onSort={() => {
                          setSort((current) =>
                            toggleSortState(current, 'outcome'),
                          );
                          setPage(1);
                        }}
                      >
                        Outcome
                      </TH>
                    )}
                    <TH width={200} align="right">
                      Actions
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {loading ? (
                    <TableSkeletonRows cols={columnCount} />
                  ) : (
                    rows.map((row) => (
                      <TR
                        key={row.order_id}
                        selected={
                          detailOpen && detailOrder?.order_id === row.order_id
                        }
                        onActivate={() => openDetail(row.order_id)}
                      >
                        <TD>
                          <TwoLine
                            primary={
                              <span className="v2-num">#{row.order_id}</span>
                            }
                            secondary={row.channel_order_id}
                          />
                        </TD>
                        <TD truncate className="text-v2-fg-2">
                          {row.store_name || <Dash />}
                        </TD>
                        <TD num align="right" className="font-medium">
                          {formatCents(row.total_price_cents)}
                        </TD>
                        <TD num align="right" className="text-v2-fg-2">
                          {formatCents(row.succeeded_refunds_total_cents)}
                          {row.is_fully_refunded && (
                            <Tag className="ml-1.5">Full</Tag>
                          )}
                        </TD>
                        <TD truncate>
                          <StatusLabel
                            size="sm"
                            tone={statusTone(row.order_status)}
                          >
                            {row.order_status}
                          </StatusLabel>
                        </TD>
                        <TD truncate>
                          {row.external_status ? (
                            <StatusLabel
                              size="sm"
                              tone={statusTone(row.external_status)}
                            >
                              {row.external_status}
                            </StatusLabel>
                          ) : (
                            <Dash />
                          )}
                        </TD>
                        {showOutcome && (
                          <TD>
                            {row.review ? (
                              <StatusLabel
                                size="sm"
                                tone={outcomeTone(row.review.outcome)}
                              >
                                {outcomeLabel(row.review.outcome)}
                              </StatusLabel>
                            ) : (
                              <Dash />
                            )}
                          </TD>
                        )}
                        <TD align="right">
                          <div className="flex items-center justify-end gap-2">
                            {canIssueRefund(row) && (
                              <Button
                                size="sm"
                                variant="primary"
                                onClick={() => openRefundModal(row)}
                              >
                                Refund
                              </Button>
                            )}
                            {canMarkReviewed(row) && (
                              <Button
                                size="sm"
                                onClick={() => openMarkModal(row)}
                              >
                                Mark reviewed
                              </Button>
                            )}
                          </div>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}

        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={REFUND_REVIEW_PAGE_SIZE}
          onPage={setPage}
          noun="orders"
        />
      </ListPage>

      <Dialog
        open={detailOpen}
        onClose={closeDetail}
        size="lg"
        title={`Order #${detailOrder?.order_id ?? '…'}`}
        footer={
          !loadingDetail && detailOrder && canMarkReviewed(detailOrder) ? (
            <>
              <Button onClick={() => openMarkModal(detailOrder)}>
                Mark reviewed
              </Button>
              {canIssueRefund(detailOrder) && (
                <Button
                  variant="primary"
                  onClick={() => openRefundModal(detailOrder)}
                >
                  Issue refund
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {loadingDetail ? (
          <div
            role="status"
            className="flex items-center justify-center gap-2 py-16 text-v2-13 text-v2-fg-3"
          >
            <Spinner />
            Loading…
          </div>
        ) : detailOrder ? (
          <OrderDetail order={detailOrder} />
        ) : null}
      </Dialog>

      <Dialog
        open={!!refundTarget}
        onClose={closeRefundModal}
        dismissible={!refundLoading}
        size="sm"
        title="Issue refund"
        description={
          refundTarget
            ? `Order #${refundTarget.order_id} · ${
                refundTarget.store_name || 'Unknown Store'
              }`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={closeRefundModal}
              disabled={refundLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitRefund}
              loading={refundLoading}
            >
              {refundLoading ? 'Processing…' : 'Confirm refund'}
            </Button>
          </>
        }
      >
        {refundTarget && (
          <div className="flex flex-col gap-4">
            <dl className="grid grid-cols-3 gap-3 border-y border-v2-line py-3">
              <Fact
                label="Order total"
                value={
                  <span className="v2-num">
                    {formatCents(refundTarget.total_price_cents)}
                  </span>
                }
              />
              <Fact
                label="Already refunded"
                value={
                  <span className="v2-num">
                    {formatCents(refundTarget.succeeded_refunds_total_cents)}
                  </span>
                }
              />
              <Fact
                label="Remaining"
                value={
                  <span className="v2-num text-v2-accent">
                    {formatCents(remainingCents(refundTarget))}
                  </span>
                }
              />
            </dl>

            <Field label="Refund amount ($)">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={refundAmountInput}
                  onChange={(e) => setRefundAmountInput(e.target.value)}
                  data-autofocus="true"
                  className="v2-num"
                />
              )}
            </Field>

            <Field label="Notes" optional>
              {(id, describedBy) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  value={refundNotes}
                  onChange={(e) => setRefundNotes(e.target.value)}
                  placeholder="Reason for refund…"
                />
              )}
            </Field>

            {refundError && <Callout tone="danger">{refundError}</Callout>}
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!markTarget}
        onClose={closeMarkModal}
        dismissible={!markLoading}
        size="sm"
        title="Mark as reviewed"
        description={
          markTarget
            ? `Order #${markTarget.order_id} · ${
                markTarget.store_name || 'Unknown Store'
              }`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={closeMarkModal}
              disabled={markLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitMark}
              loading={markLoading}
            >
              {markLoading ? 'Saving…' : 'Confirm'}
            </Button>
          </>
        }
      >
        {markTarget && (
          <div className="flex flex-col gap-4">
            <fieldset>
              <legend className="mb-2.5 text-v2-12 font-medium text-v2-fg-2">
                Outcome
              </legend>
              <div className="flex flex-col gap-3">
                {MARK_OUTCOME_OPTIONS.map((option) => (
                  <Radio
                    key={option.value}
                    name="outcome"
                    value={option.value}
                    label={option.label}
                    checked={markOutcome === option.value}
                    onChange={() => setMarkOutcome(option.value)}
                  />
                ))}
              </div>
            </fieldset>

            <Field label="Notes" optional>
              {(id, describedBy) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  value={markNotes}
                  onChange={(e) => setMarkNotes(e.target.value)}
                  placeholder="Optional notes…"
                />
              )}
            </Field>

            {markError && <Callout tone="danger">{markError}</Callout>}
          </div>
        )}
      </Dialog>
    </>
  );
}

const OrderDetail: React.FC<{ order: RefundReviewDetail }> = ({ order }) => (
  <>
    <dl className="grid grid-cols-2 gap-3 border-y border-v2-line py-3 sm:grid-cols-4">
      <Fact label="Store" value={order.store_name || '—'} />
      <Fact label="Store ID" value={order.store_id?.toString() || '—'} />
      <Fact
        label="Order total"
        value={
          <span className="v2-num">{formatCents(order.total_price_cents)}</span>
        }
      />
      <Fact
        label="Refunded"
        value={
          <span className="v2-num">
            {formatCents(order.succeeded_refunds_total_cents)}
          </span>
        }
      />
    </dl>

    <dl className="mt-2">
      <Row label="Order status">
        <StatusLabel size="sm" tone={statusTone(order.order_status)}>
          {order.order_status}
        </StatusLabel>
      </Row>
      <Row label="External status" empty={!order.external_status}>
        {order.external_status || '—'}
      </Row>
      <Row label="Channel order" empty={!order.channel_order_id}>
        {order.channel_order_id || '—'}
      </Row>
      <Row label="External order" empty={!order.external_order_id}>
        {order.external_order_id || '—'}
      </Row>
      <Row label="Paid">{order.is_paid ? 'Yes' : 'No'}</Row>
      <Row label="Fully refunded">{order.is_fully_refunded ? 'Yes' : 'No'}</Row>
      {order.stripe_payment_intent_id && (
        <Row label="Stripe PI">
          <span className="v2-num break-all text-v2-12">
            {order.stripe_payment_intent_id}
          </span>
        </Row>
      )}
    </dl>

    {order.review && (
      <section aria-label="Review" className="mt-4">
        <SectionTitle>Review</SectionTitle>
        <dl className="mt-1">
          <Row label="Outcome">
            <StatusLabel size="sm" tone={outcomeTone(order.review.outcome)}>
              {outcomeLabel(order.review.outcome)}
            </StatusLabel>
          </Row>
          <Row label="Reviewed by">{order.review.reviewed_by_email}</Row>
          <Row label="Date">
            <span className="v2-num">
              {formatReviewedAt(order.review.reviewed_at)}
            </span>
          </Row>
          {order.review.notes && (
            <Row label="Notes">
              <span className="whitespace-pre-wrap">{order.review.notes}</span>
            </Row>
          )}
        </dl>
      </section>
    )}

    {order.refunds && order.refunds.length > 0 && (
      <section aria-label="Refund history" className="mt-4">
        <SectionTitle>Refund history</SectionTitle>
        <ul className="mt-1">
          {order.refunds.map((r) => (
            <li
              key={r.id}
              className="flex items-start justify-between gap-4 border-b border-v2-line/70 py-2 last:border-b-0"
            >
              <span className="flex items-center gap-3">
                <span className="v2-num text-v2-13 font-medium text-v2-fg">
                  {formatCents(r.amount_cents)}
                </span>
                <StatusLabel size="sm" tone={statusTone(r.status)}>
                  {r.status}
                </StatusLabel>
              </span>
              <span className="min-w-0 text-right text-v2-12 text-v2-fg-3">
                <span className="v2-num block">
                  {formatRefundedAt(r.created_at)}
                </span>
                {r.stripe_refund_id && (
                  <span className="v2-num block break-all text-v2-11 text-v2-fg-4">
                    {r.stripe_refund_id}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>
    )}
  </>
);
